import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import { canvasEventEmitter } from "../lib/ai/tools/canvasStreamingTool";
import { useAuthStore } from "../store/authStore";
import { useThreadStore } from "../store/threadStore";

const CanvasContext = createContext();

export const useCanvas = () => {
  const context = useContext(CanvasContext);
  if (!context) {
    // Return a safe fallback instead of throwing to prevent crashes
    // This can happen if CanvasProvider is not in the component tree
    // Note: We don't log a warning here because components like AppSidebar and MainLayoutContent
    // may call useCanvas via useNewChat just to check canvas state, which is safe with the fallback
    const fallbackCanvasState = {
      isOpen: false,
      content: "",
      type: "text",
      language: "javascript",
      name: "",
      version: 1,
      isDirty: false,
      isLoading: false,
      error: null,
      metadata: {},
      isStreaming: false,
      sourceMessageId: null,
      originalContent: "",
      versionHistory: [],
      currentVersionId: null,
      thread_id: null,
      workspace_id: null,
      canvasId: null,
    };
    const noop = () => {};
    const noopAsync = () => Promise.resolve(false);
    return {
      canvasState: fallbackCanvasState,
      editorState: {
        selection: null,
        cursorPosition: 0,
        scrollPosition: 0,
        findText: "",
        replaceText: "",
      },
      collaborationState: {
        users: [],
        isCollaborating: false,
      },
      showPreview: false,
      setShowPreview: noop,
      openCanvas: () => {
        console.warn(
          "🎨 [CanvasContext] openCanvas called but CanvasProvider not available"
        );
      },
      closeCanvas: () => {
        console.warn(
          "🎨 [CanvasContext] closeCanvas called but CanvasProvider not available"
        );
      },
      updateContent: noop,
      saveCanvas: noopAsync,
      resetCanvas: noop,
      copyCanvasContent: noop,
      downloadCanvasContent: noop,
      setEditorState: noop,
      setCollaborationState: noop,
      canvasContent: "",
      canvasName: "",
      canvasVersion: 1,
      canvasType: "text",
      canvasLanguage: "javascript",
      isCanvasOpen: false,
      canvasIsDirty: false,
      canvasIsLoading: false,
      canvasError: null,
      canvasSourceMessageId: null,
      canvasVersionHistory: [],
      canvasCurrentVersionId: null,
      loadCanvasVersion: noopAsync,
      getAvailableVersions: () => [],
      renameCanvas: noop,
    };
  }
  return context;
};

export const CanvasProvider = ({ children }) => {
  // Track message IDs where user manually closed the canvas to prevent auto-reopening
  const [manuallyClosedMessages, setManuallyClosedMessages] = useState(
    new Set()
  );

  const [canvasState, setCanvasState] = useState({
    isOpen: false,
    content: "", // Store as markdown string
    type: "text", // 'text', 'code', 'markdown', 'html'
    language: "javascript",
    name: "", // Canvas name/title
    version: 1, // Canvas version number
    isDirty: false,
    isLoading: false,
    error: null,
    metadata: {},
    isStreaming: false,
    sourceMessageId: null, // ID of the message containing the canvas tool call
    originalContent: "", // Original content for reset functionality
    versionHistory: [], // Track all versions with their message IDs and metadata
    currentVersionId: null, // ID of the current version
    thread_id: null, // Thread ID for creating new version messages
    workspace_id: null, // Workspace ID for creating new version messages
    canvasId: null, // Unique identifier for this canvas across all versions
  });

  const [showPreview, setShowPreview] = useState(false);

  const [editorState, setEditorState] = useState({
    selection: null,
    cursorPosition: 0,
    scrollPosition: 0,
    findText: "",
    replaceText: "",
  });

  const [collaborationState, setCollaborationState] = useState({
    collaborators: [],
    activeUsers: [],
    permissions: "edit", // 'view', 'edit', 'admin'
  });

  const { user } = useAuthStore();

  // Listen to canvas tool events
  useEffect(() => {
    const handleCanvasCreate = (data) => {
      // Extract actual canvas data from nested structure
      let actualCanvasData = data;

      // Check if data is nested in metadata.originalData.canvasData
      if (data.metadata?.originalData?.canvasData) {
        actualCanvasData = {
          ...data.metadata.originalData.canvasData,
          metadata: {
            ...data.metadata,
            ...data.metadata.originalData.canvasData.metadata,
          },
        };
      }
      // Check if data is nested in canvasData property
      else if (data.canvasData) {
        actualCanvasData = {
          ...data.canvasData,
          metadata: {
            ...data.metadata,
            ...data.canvasData.metadata,
          },
        };
      }

      // Extract sourceMessageId from various possible locations
      const sourceMessageId =
        data.sourceMessageId ||
        actualCanvasData.sourceMessageId ||
        data.metadata?.sourceMessageId ||
        actualCanvasData.metadata?.sourceMessageId ||
        null;

      // CRITICAL: Check if canvas should auto-open based on user intent
      // Only open canvas if canvas mode was enabled OR user message mentions canvas
      const threadStore = useThreadStore?.getState?.();
      const messages = threadStore?.messages || [];

      // Find the user message that triggered this canvas
      let shouldAutoOpen = false;
      if (sourceMessageId) {
        const assistantMessageIndex = messages.findIndex(
          (m) => m.id === sourceMessageId
        );
        if (assistantMessageIndex > 0) {
          // Find the previous user message
          for (let i = assistantMessageIndex - 1; i >= 0; i--) {
            const msg = messages[i];
            if (msg && (msg.role === "user" || !msg.role)) {
              const canvasModeWasEnabled =
                msg.metadata?.canvasModeEnabled === true;
              const userContent = msg.content?.toLowerCase() || "";
              const mentionsCanvas =
                userContent.includes("canvas") ||
                userContent.includes("use canvas") ||
                userContent.includes("open canvas") ||
                userContent.includes("create canvas") ||
                userContent.includes("in canvas");

              shouldAutoOpen = canvasModeWasEnabled || mentionsCanvas;

              console.log(
                "🎨 [CanvasContext] Canvas auto-open check in handleCanvasCreate:",
                {
                  sourceMessageId,
                  userMessageId: msg.id,
                  canvasModeWasEnabled,
                  mentionsCanvas,
                  shouldAutoOpen,
                }
              );
              break;
            }
          }
        }
      }

      // If shouldAutoOpen is false, don't open the canvas
      if (!shouldAutoOpen) {
        console.log(
          "🎨 [CanvasContext] Skipping canvas auto-open - not explicitly requested"
        );
        return;
      }

      const content = actualCanvasData.content || "";
      const isStreaming = actualCanvasData.metadata?.isStreaming || false;

      console.log(
        "🎨 [CanvasContext] handleCanvasCreate - processing canvas data:",
        {
          hasContent: !!content,
          contentLength: content?.length,
          contentPreview: content?.substring(0, 100),
          isStreaming,
          sourceMessageId,
          canvasName: actualCanvasData.name,
        }
      );

      // Get thread and workspace IDs from threadStore (already declared above)
      const thread_id = threadStore?.currentThreadId || null;
      const workspace_id =
        threadStore?.currentThread?.workspace_id ||
        threadStore?.currentWorkspaceId ||
        null;
      const canvasId = sourceMessageId || `canvas_${Date.now()}`;

      setCanvasState((prev) => ({
        ...prev,
        isOpen: true,
        content: content,
        type: actualCanvasData.type || "text",
        language: actualCanvasData.language || "javascript",
        name: actualCanvasData.name || "Canvas",
        version: actualCanvasData.version || 1,
        metadata: {
          ...actualCanvasData.metadata,
          lastUpdateSource: "canvas_create",
        },
        isDirty: false,
        error: null,
        isStreaming: isStreaming,
        originalContent: content,
        sourceMessageId: sourceMessageId, // Ensure sourceMessageId is set immediately
        thread_id: thread_id,
        workspace_id: workspace_id,
        canvasId: canvasId, // Use first message ID as permanent canvas ID
        // Initialize version history with proper sourceMessageId
        versionHistory: [
          {
            id:
              sourceMessageId ||
              `version_${Date.now()}_v${actualCanvasData.version || 1}`,
            version: actualCanvasData.version || 1,
            name: actualCanvasData.name || "Canvas",
            content: content,
            timestamp: new Date().toISOString(),
            messageId: sourceMessageId,
            type: actualCanvasData.type || "text",
            language: actualCanvasData.language || "javascript",
            metadata: {
              ...actualCanvasData.metadata,
              isInitialVersion: true,
              createdFromTool: true,
            },
          },
        ],
        currentVersionId:
          sourceMessageId ||
          `version_${Date.now()}_v${actualCanvasData.version || 1}`,
      }));

      // Dispatch event to reset undo history for initial content
      try {
        const resetUndoEvent = new CustomEvent("canvas-reset-undo-history", {
          detail: {
            source: "canvas_create",
            timestamp: new Date().toISOString(),
            reason: "initial_content_loaded",
          },
        });
        window.dispatchEvent(resetUndoEvent);
      } catch (error) {
        console.warn("Could not dispatch reset undo history event:", error);
      }

      // Dispatch scroll to bottom event when canvas opens
      try {
        const scrollEvent = new CustomEvent("canvas-opened-scroll-to-bottom", {
          detail: {
            source: "canvas-create",
            timestamp: new Date().toISOString(),
            canvasName: actualCanvasData.name || "Canvas",
          },
        });
        window.dispatchEvent(scrollEvent);
      } catch (error) {
        console.warn("Could not dispatch scroll to bottom event:", error);
      }
    };

    const handleCanvasStream = (data) => {
      // CRITICAL: Check if canvas should auto-open based on user intent
      // Only open canvas if canvas mode was enabled OR user message mentions canvas
      const threadStore = useThreadStore?.getState?.();
      const messages = threadStore?.messages || [];

      // Extract sourceMessageId to find the user message
      const sourceMessageId =
        data.sourceMessageId ||
        data.metadata?.sourceMessageId ||
        canvasState.sourceMessageId ||
        null;

      let shouldAutoOpen = false;
      if (sourceMessageId) {
        const assistantMessageIndex = messages.findIndex(
          (m) => m.id === sourceMessageId
        );
        if (assistantMessageIndex > 0) {
          for (let i = assistantMessageIndex - 1; i >= 0; i--) {
            const msg = messages[i];
            if (msg && (msg.role === "user" || !msg.role)) {
              const canvasModeWasEnabled =
                msg.metadata?.canvasModeEnabled === true;
              const userContent = msg.content?.toLowerCase() || "";
              const mentionsCanvas =
                userContent.includes("canvas") ||
                userContent.includes("use canvas") ||
                userContent.includes("open canvas") ||
                userContent.includes("create canvas") ||
                userContent.includes("in canvas");

              shouldAutoOpen = canvasModeWasEnabled || mentionsCanvas;
              break;
            }
          }
        }
      }

      // If shouldAutoOpen is false and canvas is not already open, don't open it
      if (!shouldAutoOpen && !canvasState.isOpen) {
        console.log(
          "🎨 [CanvasContext] Skipping canvas stream auto-open - not explicitly requested"
        );
        return;
      }

      setCanvasState((prev) => {
        // Extract sourceMessageId from stream data if available
        const extractedSourceMessageId =
          data.sourceMessageId ||
          data.metadata?.sourceMessageId ||
          prev.sourceMessageId ||
          null;

        // Check if this is a meaningful content update
        const hasContentChange = data.content !== prev.content;
        const hasTypeChange = data.type && data.type !== prev.type;
        const hasStreamingStateChange = !data.isComplete !== prev.isStreaming;
        const hasSourceMessageIdChange =
          extractedSourceMessageId !== prev.sourceMessageId;

        // Only update if there are meaningful changes
        if (
          !hasContentChange &&
          !hasTypeChange &&
          !hasStreamingStateChange &&
          !hasSourceMessageIdChange &&
          prev.isOpen
        ) {
          return prev; // No meaningful changes, return previous state
        }

        // CRITICAL: During streaming and when streaming completes, do NOT mark as dirty
        // The content being streamed is AI-generated, not user-edited
        // Only mark as dirty when lastUpdateSource is "user" (actual user edits)
        const isStreamingComplete = data.isComplete === true;
        const isCurrentlyStreaming = !data.isComplete;

        // Only preserve dirty state if it was from a user edit, otherwise reset to false
        // During streaming or when streaming completes, content is AI-generated, so not dirty
        const shouldBeDirty =
          prev.metadata?.lastUpdateSource === "user" && prev.isDirty;

        const newState = {
          ...prev,
          isOpen: shouldAutoOpen || prev.isOpen, // Only open if should auto-open or already open
          content: data.content || prev.content,
          type: data.type || prev.type,
          // During streaming or when streaming completes, don't mark as dirty unless it was a user edit
          isDirty: shouldBeDirty,
          isStreaming: isCurrentlyStreaming,
          sourceMessageId: extractedSourceMessageId,
          metadata: {
            ...prev.metadata,
            lastStreamUpdate: new Date().toISOString(),
            lastUpdateSource: "stream",
          },
        };

        // If this is the first stream and we don't have version history, initialize it
        if (prev.versionHistory.length === 0 && sourceMessageId) {
          newState.versionHistory = [
            {
              id: sourceMessageId,
              version: 1,
              name: prev.name || "Canvas",
              content: data.content || "",
              timestamp: new Date().toISOString(),
              messageId: sourceMessageId,
              type: data.type || prev.type || "text",
              language: prev.language || "javascript",
              metadata: {
                ...prev.metadata,
                isInitialVersion: true,
                createdFromStream: true,
              },
            },
          ];
          newState.currentVersionId = sourceMessageId;
          newState.originalContent = data.content || "";
        }

        return newState;
      });

      // Dispatch event to reset undo history when streaming starts (first stream event)
      if (!canvasState.isOpen || !canvasState.isStreaming) {
        try {
          const resetUndoEvent = new CustomEvent("canvas-reset-undo-history", {
            detail: {
              source: "canvas_stream_start",
              timestamp: new Date().toISOString(),
              reason: "streaming_started",
            },
          });
          window.dispatchEvent(resetUndoEvent);
        } catch (error) {
          console.warn("Could not dispatch reset undo history event:", error);
        }
      }

      // Dispatch scroll to bottom event when canvas stream starts (if not already open)
      if (!canvasState.isOpen) {
        try {
          const scrollEvent = new CustomEvent(
            "canvas-opened-scroll-to-bottom",
            {
              detail: {
                source: "canvas-stream",
                timestamp: new Date().toISOString(),
                isStreaming: !data.isComplete,
              },
            }
          );
          window.dispatchEvent(scrollEvent);
        } catch (error) {
          console.warn("Could not dispatch scroll to bottom event:", error);
        }
      }
    };

    const handleCanvasUpdate = (data) => {
      setCanvasState((prev) => {
        // Create new version entry
        const newVersionEntry = {
          id: `version_${Date.now()}_v${data.version || prev.version}`,
          version: data.version || prev.version,
          name: data.name || prev.name,
          content: data.content,
          timestamp: new Date().toISOString(),
          messageId: prev.sourceMessageId,
          type: data.type || prev.type,
          language: data.language || prev.language,
          metadata: {
            ...prev.metadata,
            ...data.metadata,
            isEdit: true,
            editedFrom: prev.currentVersionId,
          },
        };

        return {
          ...prev,
          content: data.content,
          name: data.name || prev.name,
          version: data.version || prev.version,
          isDirty: true,
          currentVersionId: newVersionEntry.id,
          versionHistory: [...prev.versionHistory, newVersionEntry],
          metadata: {
            ...prev.metadata,
            ...data.metadata,
            lastUpdated: new Date().toISOString(),
          },
        };
      });
    };

    const handleCanvasClose = (data) => {
      // Restore body scrolling when canvas is closed
      document.body.style.overflow = "";

      setCanvasState((prev) => ({
        ...prev,
        isOpen: false,
        content: "",
        name: "",
        version: 1,
        isDirty: false,
        error: null,
        isStreaming: false,
        versionHistory: [],
        currentVersionId: null,
        thread_id: null,
        workspace_id: null,
        canvasId: null,
      }));
    };

    const handleCanvasSave = (data) => {
      setCanvasState((prev) => ({
        ...prev,
        isDirty: false,
        metadata: {
          ...prev.metadata,
          lastSaved: new Date().toISOString(),
        },
      }));
    };

    // Handle canvas stream updates (new streaming format)
    const handleCanvasStreamUpdate = (data) => {
      // CRITICAL: Check if canvas should auto-open based on user intent
      // Only open canvas if canvas mode was enabled OR user message mentions canvas
      const threadStore = useThreadStore?.getState?.();
      const messages = threadStore?.messages || [];

      // Extract sourceMessageId to find the user message
      const sourceMessageId =
        data.sourceMessageId ||
        data.metadata?.sourceMessageId ||
        canvasState.sourceMessageId ||
        null;

      let shouldAutoOpen = false;
      if (sourceMessageId) {
        const assistantMessageIndex = messages.findIndex(
          (m) => m.id === sourceMessageId
        );
        if (assistantMessageIndex > 0) {
          for (let i = assistantMessageIndex - 1; i >= 0; i--) {
            const msg = messages[i];
            if (msg && (msg.role === "user" || !msg.role)) {
              const canvasModeWasEnabled =
                msg.metadata?.canvasModeEnabled === true;
              const userContent = msg.content?.toLowerCase() || "";
              const mentionsCanvas =
                userContent.includes("canvas") ||
                userContent.includes("use canvas") ||
                userContent.includes("open canvas") ||
                userContent.includes("create canvas") ||
                userContent.includes("in canvas");

              shouldAutoOpen = canvasModeWasEnabled || mentionsCanvas;
              break;
            }
          }
        }
      }

      // If shouldAutoOpen is false and canvas is not already open, don't open it
      if (!shouldAutoOpen && !canvasState.isOpen) {
        console.log(
          "🎨 [CanvasContext] Skipping canvas stream update auto-open - not explicitly requested"
        );
        return;
      }

      setCanvasState((prev) => {
        // Extract sourceMessageId from stream update data if available
        const extractedSourceMessageId =
          data.sourceMessageId ||
          data.metadata?.sourceMessageId ||
          prev.sourceMessageId ||
          null;

        // CRITICAL: During stream updates, do NOT mark as dirty
        // The content being updated is AI-generated, not user-edited
        // Only preserve dirty state if it was from a user edit
        const shouldBeDirty =
          prev.metadata?.lastUpdateSource === "user" && prev.isDirty;

        const newState = {
          ...prev,
          isOpen: shouldAutoOpen || prev.isOpen, // Only open if should auto-open or already open
          content: data.content || prev.content,
          isStreaming: !data.isComplete,
          isDirty: shouldBeDirty, // Only dirty if it was a user edit, otherwise false
          sourceMessageId: extractedSourceMessageId, // Ensure sourceMessageId is set
          metadata: {
            ...prev.metadata,
            lastStreamUpdate: new Date().toISOString(),
            lastUpdateSource: "stream",
          },
        };

        // If this is the first stream update and we don't have version history, initialize it
        if (prev.versionHistory.length === 0 && extractedSourceMessageId) {
          newState.versionHistory = [
            {
              id: extractedSourceMessageId,
              version: 1,
              name: prev.name || "Canvas",
              content: data.content || "",
              timestamp: new Date().toISOString(),
              messageId: extractedSourceMessageId,
              type: prev.type || "text",
              language: prev.language || "javascript",
              metadata: {
                ...prev.metadata,
                isInitialVersion: true,
                createdFromStreamUpdate: true,
              },
            },
          ];
          newState.currentVersionId = extractedSourceMessageId;
          newState.originalContent = data.content || "";
        }

        return newState;
      });

      // Dispatch scroll to bottom event when canvas stream update starts (if not already open)
      if (!canvasState.isOpen) {
        try {
          const scrollEvent = new CustomEvent(
            "canvas-opened-scroll-to-bottom",
            {
              detail: {
                source: "canvas-stream-update",
                timestamp: new Date().toISOString(),
                isComplete: data.isComplete,
              },
            }
          );
          window.dispatchEvent(scrollEvent);
        } catch (error) {
          console.warn("Could not dispatch scroll to bottom event:", error);
        }
      }
    };

    // Handle canvas stream completion (new streaming format)
    const handleCanvasStreamComplete = (data) => {
      setCanvasState((prev) => {
        // Extract sourceMessageId from stream completion data if available
        const sourceMessageId =
          data.sourceMessageId ||
          data.metadata?.sourceMessageId ||
          prev.sourceMessageId ||
          null;

        const finalContent = data.content || prev.content;

        // CRITICAL: When streaming completes, the content is AI-generated, not user-edited
        // So it should NOT be marked as dirty. Only mark as dirty if there was a user edit.
        const shouldBeDirty =
          prev.metadata?.lastUpdateSource === "user" && prev.isDirty;

        return {
          ...prev,
          content: finalContent,
          originalContent: finalContent, // Update originalContent to match final streamed content
          isStreaming: false,
          isDirty: shouldBeDirty, // Only dirty if it was a user edit, otherwise false
          sourceMessageId: sourceMessageId,
          metadata: {
            ...prev.metadata,
            lastStreamComplete: new Date().toISOString(),
            lastUpdateSource: "stream_complete",
          },
        };
      });

      // Dispatch event to reset undo history when streaming completes
      try {
        const resetUndoEvent = new CustomEvent("canvas-reset-undo-history", {
          detail: {
            source: "canvas_stream_complete",
            timestamp: new Date().toISOString(),
            reason: "streaming_complete",
          },
        });
        window.dispatchEvent(resetUndoEvent);
      } catch (error) {
        console.warn("Could not dispatch reset undo history event:", error);
      }
    };

    // Handle canvas edit events
    const handleCanvasEdit = (data) => {
      setCanvasState((prev) => {
        const newContent = data.content || prev.content;
        const newVersion = data.version || prev.version;

        // Extract sourceMessageId from edit data if available
        const sourceMessageId =
          data.sourceMessageId ||
          data.metadata?.sourceMessageId ||
          prev.sourceMessageId ||
          null;

        // Add to version history
        const newVersionEntry = {
          id: `edit_${Date.now()}`,
          version: newVersion,
          name: prev.name,
          content: newContent,
          timestamp: new Date().toISOString(),
          type: prev.type,
          language: prev.language,
          messageId: sourceMessageId, // Include messageId for version history
          metadata: {
            ...prev.metadata,
            ...data.metadata,
            editType: data.editType,
            editQuery: data.editQuery,
            isEdit: true,
          },
        };

        const newVersionHistory = [...prev.versionHistory, newVersionEntry];

        return {
          ...prev,
          content: newContent,
          version: newVersion,
          isDirty: true,
          sourceMessageId: sourceMessageId, // Ensure sourceMessageId is set
          versionHistory: newVersionHistory,
          currentVersionId: newVersionEntry.id,
          metadata: {
            ...prev.metadata,
            ...data.metadata,
            lastEdit: {
              query: data.editQuery,
              type: data.editType,
              timestamp: new Date().toISOString(),
            },
            version: newVersion,
          },
        };
      });

      // Dispatch scroll to bottom event when canvas edit occurs (if not already open)
      if (!canvasState.isOpen) {
        try {
          const scrollEvent = new CustomEvent(
            "canvas-opened-scroll-to-bottom",
            {
              detail: {
                source: "canvas-edit",
                timestamp: new Date().toISOString(),
                editType: data.editType,
                version: data.version,
              },
            }
          );
          window.dispatchEvent(scrollEvent);
        } catch (error) {
          console.warn("Could not dispatch scroll to bottom event:", error);
        }
      }
    };

    // Handle canvas title update (auto-generated after content creation)
    const handleCanvasTitleUpdate = (data) => {
      if (!data.title) return;

      setCanvasState((prev) => {
        // Only update if this is for the current canvas
        if (
          data.sourceMessageId &&
          data.sourceMessageId !== prev.sourceMessageId
        ) {
          return prev;
        }

        console.log("🎨 [CanvasContext] Auto-updating canvas title:", {
          oldTitle: prev.name,
          newTitle: data.title,
          sourceMessageId: data.sourceMessageId,
        });

        return {
          ...prev,
          name: data.title,
          metadata: {
            ...prev.metadata,
            titleAutoGenerated: true,
            titleGeneratedAt: new Date().toISOString(),
          },
        };
      });
    };

    // Register event listeners
    canvasEventEmitter.on("canvas_create", handleCanvasCreate);
    canvasEventEmitter.on("canvas_edit", handleCanvasEdit);
    canvasEventEmitter.on("canvas_stream", handleCanvasStream);
    canvasEventEmitter.on("canvas_update", handleCanvasUpdate);
    canvasEventEmitter.on("canvas_close", handleCanvasClose);
    canvasEventEmitter.on("canvas_save", handleCanvasSave);
    canvasEventEmitter.on("canvas_stream_update", handleCanvasStreamUpdate);
    canvasEventEmitter.on("canvas_stream_complete", handleCanvasStreamComplete);
    canvasEventEmitter.on("canvas_title_update", handleCanvasTitleUpdate);

    // Cleanup
    return () => {
      canvasEventEmitter.off("canvas_create", handleCanvasCreate);
      canvasEventEmitter.off("canvas_edit", handleCanvasEdit);
      canvasEventEmitter.off("canvas_stream", handleCanvasStream);
      canvasEventEmitter.off("canvas_update", handleCanvasUpdate);
      canvasEventEmitter.off("canvas_close", handleCanvasClose);
      canvasEventEmitter.off("canvas_save", handleCanvasSave);
      canvasEventEmitter.off("canvas_stream_update", handleCanvasStreamUpdate);
      canvasEventEmitter.off(
        "canvas_stream_complete",
        handleCanvasStreamComplete
      );
      canvasEventEmitter.off("canvas_title_update", handleCanvasTitleUpdate);
    };
  }, []);

  // Open canvas with content
  const openCanvas = useCallback(
    ({
      content,
      type = "text",
      language = "javascript",
      name = "Canvas",
      version = 1,
      metadata = {},
      sourceMessageId = null,
      thread_id = null,
      workspace_id = null,
      canvasId = null,
    }) => {
      // EXPECTED BEHAVIOR: When opening a canvas, it replaces any currently open canvas.
      // This allows users to switch between multiple canvases in a thread by clicking
      // different preview buttons. Only one canvas can be open at a time.

      // If opening canvas from a message, clear the manually closed flag
      // This allows user to reopen by clicking the preview
      if (sourceMessageId) {
        setManuallyClosedMessages((prevSet) => {
          const newSet = new Set(prevSet);
          const wasClosed = newSet.has(sourceMessageId);
          newSet.delete(sourceMessageId);
          if (wasClosed) {
            console.log(
              "🎨 [CanvasContext] Cleared manually closed flag for message:",
              {
                messageId: sourceMessageId,
                wasManuallyClosed: true,
              }
            );
          }
          return newSet;
        });
      }

      // Close all sidebars when opening canvas
      try {
        const closeSidebarsEvent = new CustomEvent("close-all-sidebars", {
          detail: { source: "canvas-open" },
        });
        window.dispatchEvent(closeSidebarsEvent);
      } catch (error) {
        console.warn("Could not dispatch close sidebars event:", error);
      }

      // Ensure content is a string (markdown)
      let markdownContent = "";
      if (typeof content === "string") {
        markdownContent = content;
      } else if (Array.isArray(content)) {
        // If content is blocks, we'll let the TextEditor handle conversion
        markdownContent = JSON.stringify(content);
      } else {
        markdownContent = String(content || "");
      }

      // Get thread and workspace IDs from threadStore if not provided
      const threadStore = useThreadStore?.getState?.() || {};
      const finalThreadId = thread_id || threadStore.currentThreadId || null;
      const finalWorkspaceId =
        workspace_id ||
        threadStore.currentThread?.workspace_id ||
        threadStore.currentWorkspaceId ||
        null;
      const finalCanvasId =
        canvasId || sourceMessageId || `canvas_${Date.now()}`;

      console.log("🎨 [CanvasContext] Opening canvas with content:", {
        hasContent: !!markdownContent,
        contentLength: markdownContent?.length,
        sourceMessageId,
        finalThreadId,
        finalWorkspaceId,
        finalCanvasId,
      });

      setCanvasState((prev) => {
        const newState = {
          ...prev,
          isOpen: true,
          content: markdownContent,
          originalContent: markdownContent,
          type,
          language,
          name,
          version,
          isDirty: false,
          isLoading: false,
          error: null,
          sourceMessageId: sourceMessageId,
          thread_id: finalThreadId,
          workspace_id: finalWorkspaceId,
          canvasId: finalCanvasId,
          versionHistory: [
            {
              id: sourceMessageId || `version_${Date.now()}_v${version}`,
              version: version,
              name: name,
              content: markdownContent,
              timestamp: new Date().toISOString(),
              messageId: sourceMessageId,
              type: type,
              language: language,
              metadata: {
                ...metadata,
                isInitialVersion: true,
                openedDirectly: true,
              },
            },
          ],
          currentVersionId:
            sourceMessageId || `version_${Date.now()}_v${version}`,
          metadata: {
            ...prev.metadata,
            ...metadata,
            lastOpened: new Date().toISOString(),
            lastUpdateSource: "canvas_open",
          },
        };

        console.log("🎨 [CanvasContext] New canvas state set:", {
          isOpen: newState.isOpen,
          hasContent: !!newState.content,
          contentLength: newState.content?.length,
          sourceMessageId: newState.sourceMessageId,
        });

        return newState;
      });

      // Prevent body scrolling when canvas is open
      document.body.style.overflow = "hidden";

      // CRITICAL: Dispatch content initialization event BEFORE undo history reset
      // This ensures the editor receives the content immediately
      try {
        const contentInitEvent = new CustomEvent("canvas-content-initialized", {
          detail: {
            content: markdownContent,
            sourceMessageId: sourceMessageId,
            timestamp: new Date().toISOString(),
          },
        });
        window.dispatchEvent(contentInitEvent);
      } catch (error) {
        console.warn("Could not dispatch content init event:", error);
      }

      // Dispatch event to reset undo history when canvas is opened with content
      // Add a slight delay to ensure this happens after any preview-triggered events
      setTimeout(() => {
        try {
          const resetUndoEvent = new CustomEvent("canvas-reset-undo-history", {
            detail: {
              source: metadata?.fromPreview
                ? "canvas_open_from_preview"
                : "canvas_open",
              timestamp: new Date().toISOString(),
              reason: metadata?.fromPreview
                ? "canvas_opened_from_preview"
                : "canvas_opened_with_content",
              sourceMessageId: sourceMessageId,
              hasContent: !!markdownContent,
              contentLength: markdownContent?.length || 0,
            },
          });
          window.dispatchEvent(resetUndoEvent);
        } catch (error) {
          console.warn("Could not dispatch reset undo history event:", error);
        }
      }, 100); // Small delay to ensure proper event ordering

      // Dispatch scroll to bottom event when canvas opens
      try {
        const scrollEvent = new CustomEvent("canvas-opened-scroll-to-bottom", {
          detail: {
            source: "canvas-open-function",
            timestamp: new Date().toISOString(),
            canvasName: name,
            sourceMessageId: sourceMessageId,
          },
        });
        window.dispatchEvent(scrollEvent);
      } catch (error) {
        console.warn("Could not dispatch scroll to bottom event:", error);
      }
    },
    []
  );

  const closeCanvas = useCallback(() => {
    console.log("🎨 [CanvasContext] closeCanvas called");

    // Restore body scrolling when canvas is closed
    document.body.style.overflow = "";

    setCanvasState((prev) => {
      const sourceMessageId = prev.sourceMessageId;

      console.log("🎨 [CanvasContext] Setting canvas state to closed:", {
        wasOpen: prev.isOpen,
        sourceMessageId,
      });

      // Track that user manually closed canvas for this message
      if (sourceMessageId) {
        setManuallyClosedMessages((prevSet) => {
          const newSet = new Set(prevSet);
          newSet.add(sourceMessageId);
          console.log("🎨 [CanvasContext] Marked message as manually closed:", {
            messageId: sourceMessageId,
            closedMessages: Array.from(newSet),
          });
          return newSet;
        });
      }

      return {
        ...prev,
        isOpen: false,
        content: "",
        originalContent: "",
        name: "",
        version: 1,
        sourceMessageId: null,
        thread_id: null,
        workspace_id: null,
        canvasId: null,
        versionHistory: [],
        currentVersionId: null,
        isDirty: false,
        error: null,
        isStreaming: false,
      };
    });
  }, []);

  const updateContent = useCallback((newContent, source = "user") => {
    setCanvasState((prev) => ({
      ...prev,
      content: newContent,
      isDirty: source === "user",
      metadata: {
        ...prev.metadata,
        lastUpdated: new Date().toISOString(),
        lastUpdateSource: source,
        contentLength: newContent?.length || 0,
      },
    }));
  }, []);

  const saveCanvas = useCallback(async () => {
    setCanvasState((prev) => ({ ...prev, isLoading: true }));

    try {
      // Validate required data for creating new message
      if (!canvasState.thread_id || !canvasState.workspace_id) {
        console.error("Missing thread_id or workspace_id for canvas save:", {
          thread_id: canvasState.thread_id,
          workspace_id: canvasState.workspace_id,
        });

        // Try to get from threadStore as fallback
        const { useThreadStore } = await import("@/store");
        const threadStore = useThreadStore.getState();
        const thread_id = canvasState.thread_id || threadStore.currentThreadId;
        const workspace_id =
          canvasState.workspace_id ||
          threadStore.currentThread?.workspace_id ||
          threadStore.currentWorkspaceId;

        if (!thread_id || !workspace_id) {
          setCanvasState((prev) => ({
            ...prev,
            error:
              "Cannot save canvas: missing thread or workspace information",
            isLoading: false,
          }));
          return false;
        }

        // Update canvas state with retrieved IDs
        setCanvasState((prev) => ({
          ...prev,
          thread_id,
          workspace_id,
        }));
      }

      // Import required dependencies
      const { supabase } = await import("@/lib/supabase");
      const { useThreadStore } = await import("@/store");
      const { saveToolCallMessage, saveUserMessage } = await import(
        "@/lib/utils/ai/streaming/databaseOperations"
      );
      const { useAuthStore } = await import("@/store/authStore");

      const threadStore = useThreadStore.getState();
      const authStore = useAuthStore.getState();
      const userId = authStore.userId || authStore.user?.id;
      const newVersion = canvasState.version + 1;

      // Generate proper UUIDs for both messages
      const userMessageId = crypto.randomUUID();
      const assistantMessageId = crypto.randomUUID();

      // Generate AI-powered change summary
      const { generateChangeSummary } = await import(
        "@/lib/utils/canvas/changeAnalyzer"
      );

      let changeSummary = "";
      try {
        changeSummary = await generateChangeSummary({
          oldContent: canvasState.originalContent || "",
          newContent: canvasState.content || "",
          canvasType: canvasState.type,
          canvasName: canvasState.name,
          provider: {
            name: "openai",
            model: "gpt-4o-mini",
          },
        });
      } catch (error) {
        console.error(
          "🎨 [CanvasContext] Failed to generate change summary:",
          error
        );
        // Fallback to basic description
        const originalLength = canvasState.originalContent?.length || 0;
        const currentLength = canvasState.content?.length || 0;
        const lengthDiff = currentLength - originalLength;

        if (lengthDiff > 0) {
          changeSummary = `Added ${lengthDiff} characters`;
        } else if (lengthDiff < 0) {
          changeSummary = `Removed ${Math.abs(lengthDiff)} characters`;
        } else {
          changeSummary = "Formatting changes";
        }
      }

      // Calculate content metrics for metadata
      const originalLength = canvasState.originalContent?.length || 0;
      const currentLength = canvasState.content?.length || 0;
      const lengthDiff = currentLength - originalLength;

      // Create user message describing the canvas save action
      const userMessage = {
        id: userMessageId,
        message_id: userMessageId,
        thread_id: canvasState.thread_id,
        workspace_id: canvasState.workspace_id,
        user_id: userId,
        role: "user",
        content: `**${canvasState.name}** (v${canvasState.version} → v${newVersion}): ${changeSummary}`,
        metadata: {
          isCanvasSaveRequest: true,
          canvasId: canvasState.canvasId,
          canvasName: canvasState.name,
          canvasVersion: newVersion,
          previousVersion: canvasState.version,
          contentLengthChange: lengthDiff,
          originalLength: originalLength,
          newLength: currentLength,
          changeSummary: changeSummary,
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Save user message first
      const userMessageSaved = await saveUserMessage(userMessage);

      if (!userMessageSaved) {
        console.error("🎨 [CanvasContext] Failed to save user message");
        // Continue anyway - this is not critical
      }

      // For user-initiated saves, the message content should be empty
      // The canvas will appear in the card from the tool call result
      const wordCount =
        canvasState.content
          ?.trim()
          .split(/\s+/)
          .filter((word) => word.length > 0).length || 0;

      // Create new assistant message with canvas tool call
      const newMessage = {
        id: assistantMessageId,
        message_id: assistantMessageId,
        thread_id: canvasState.thread_id,
        workspace_id: canvasState.workspace_id,
        role: "assistant",
        content: "", // Empty content - canvas shows in card only
        tool_calls: [
          {
            id: `call_${Date.now()}_canvas_save`,
            type: "function",
            function: {
              name: "canvas_create",
              arguments: JSON.stringify({
                name: canvasState.name,
                type: canvasState.type,
                language: canvasState.language,
              }),
            },
            result: {
              success: true,
              message: `Canvas "${canvasState.name}" saved as version ${newVersion}`,
              canvas: {
                name: canvasState.name,
                type: canvasState.type,
                content: canvasState.content,
                version: newVersion,
                language: canvasState.language,
                wordCount: wordCount,
                metadata: {
                  ...canvasState.metadata,
                  name: canvasState.name,
                  version: newVersion,
                  wordCount: wordCount,
                  lastSaved: new Date().toISOString(),
                  savedFromCanvas: true,
                  contentLength: canvasState.content?.length || 0,
                  isUserEdit: true, // Mark as user-initiated save
                },
              },
              canvasData: {
                content: canvasState.content,
                contentType: canvasState.type,
                type: canvasState.type,
                language: canvasState.language,
                name: canvasState.name,
                title: canvasState.name,
                version: newVersion,
                wordCount: wordCount,
                metadata: {
                  ...canvasState.metadata,
                  name: canvasState.name,
                  version: newVersion,
                  wordCount: wordCount,
                  lastSaved: new Date().toISOString(),
                  savedFromCanvas: true,
                  contentLength: canvasState.content?.length || 0,
                  isUserEdit: true,
                  editHistory: [
                    ...(canvasState.metadata?.editHistory || []),
                    {
                      timestamp: new Date().toISOString(),
                      action: "save",
                      version: newVersion,
                      source: "canvas_editor",
                      wordCount: wordCount,
                    },
                  ],
                },
              },
            },
          },
        ],
        metadata: {
          canvasId: canvasState.canvasId,
          parentMessageId: canvasState.sourceMessageId,
          canvasName: canvasState.name,
          canvasVersion: newVersion,
          wordCount: wordCount,
          isCanvasSave: true,
          isUserEdit: true, // Mark as user-initiated save
          savedAt: new Date().toISOString(),
          provider: "artifact",
          model: "canvas",
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        provider: "artifact",
      };

      // Save the new message to database
      const dbSuccess = await saveToolCallMessage(newMessage);

      if (!dbSuccess) {
        console.error(
          "🎨 [CanvasContext] Failed to save new canvas message to database"
        );

        // Fall back to old update-in-place method
        console.log(
          "🎨 [CanvasContext] Attempting fallback to update-in-place method"
        );
        return await fallbackToUpdateMethod();
      }

      // Update thread store - add both user and assistant messages to the messages array
      try {
        const currentMessages = threadStore.messages || [];
        // Add both messages: user message first, then assistant message
        const updatedMessages = [...currentMessages, userMessage, newMessage];
        threadStore.setMessages(updatedMessages);
      } catch (storeError) {
        console.warn(
          "🎨 [CanvasContext] Failed to update thread store, will sync on reload:",
          storeError
        );
        // Not fatal - message is in database, will appear on refresh
      }

      // Update canvas state to reflect successful save
      setCanvasState((prev) => ({
        ...prev,
        sourceMessageId: assistantMessageId, // Now points to the new message
        version: newVersion,
        isDirty: false,
        isLoading: false,
        originalContent: prev.content, // New baseline for reset
        versionHistory: [
          ...prev.versionHistory,
          {
            id: assistantMessageId,
            version: newVersion,
            name: prev.name,
            content: prev.content,
            timestamp: new Date().toISOString(),
            messageId: assistantMessageId,
            type: prev.type,
            language: prev.language,
            metadata: {
              ...prev.metadata,
              parentMessageId: prev.sourceMessageId,
              savedFromCanvas: true,
              version: newVersion,
              userMessageId: userMessageId,
            },
          },
        ],
        currentVersionId: assistantMessageId,
        metadata: {
          ...prev.metadata,
          lastSaved: new Date().toISOString(),
          savedFromCanvas: true,
          version: newVersion,
        },
      }));

      // Dispatch scroll event to show the new message
      try {
        const scrollEvent = new CustomEvent("scroll-to-message", {
          detail: {
            messageId: assistantMessageId,
            source: "canvas-save",
            timestamp: new Date().toISOString(),
          },
        });
        window.dispatchEvent(scrollEvent);
      } catch (error) {
        console.warn("Could not dispatch scroll event:", error);
      }

      console.log(
        "🎨 [CanvasContext] Canvas saved successfully as new version"
      );
      return true;
    } catch (error) {
      console.error("🎨 [CanvasContext] Error saving canvas:", error);

      // Try fallback to update-in-place method
      try {
        console.log(
          "🎨 [CanvasContext] Attempting fallback to update-in-place method"
        );
        return await fallbackToUpdateMethod();
      } catch (fallbackError) {
        console.error(
          "🎨 [CanvasContext] Fallback method also failed:",
          fallbackError
        );
        setCanvasState((prev) => ({
          ...prev,
          error: "Failed to save canvas. Your changes are preserved locally.",
          isLoading: false,
        }));
        return false;
      }
    }

    // Fallback function to use old update-in-place method
    async function fallbackToUpdateMethod() {
      try {
        if (!canvasState.sourceMessageId) {
          setCanvasState((prev) => ({
            ...prev,
            isDirty: false,
            isLoading: false,
            metadata: {
              ...prev.metadata,
              lastSaved: new Date().toISOString(),
            },
          }));
          return true;
        }

        const { supabase } = await import("@/lib/supabase");
        const { useThreadStore } = await import("@/store");

        // Get the current message from database
        const { data: message, error: fetchError } = await supabase
          .from("thread_messages")
          .select("*")
          .eq("id", canvasState.sourceMessageId)
          .single();

        if (fetchError) {
          throw new Error(`Failed to fetch message: ${fetchError.message}`);
        }

        if (!message) {
          throw new Error("Message not found");
        }

        // Parse existing tool_calls
        let toolCalls = [];
        if (message.tool_calls) {
          try {
            toolCalls =
              typeof message.tool_calls === "string"
                ? JSON.parse(message.tool_calls)
                : message.tool_calls;
          } catch (e) {
            toolCalls = [];
          }
        }

        // Find and update the canvas tool call
        let canvasToolCallFound = false;
        const updatedToolCalls = toolCalls.map((toolCall) => {
          const toolName =
            toolCall.function?.name ||
            toolCall.name ||
            toolCall.type ||
            (toolCall.function && typeof toolCall.function === "string"
              ? toolCall.function
              : null);

          if (
            toolName === "canvas_create" ||
            toolName === "canvas_stream" ||
            toolName === "canvas_edit"
          ) {
            canvasToolCallFound = true;

            const updatedCanvasData = {
              content: canvasState.content,
              contentType: canvasState.type,
              type: canvasState.type,
              language: canvasState.language,
              name: canvasState.name,
              title: canvasState.name,
              version: canvasState.version + 1,
              metadata: {
                ...canvasState.metadata,
                lastSaved: new Date().toISOString(),
                savedFromCanvas: true,
                contentLength: canvasState.content?.length || 0,
                fallbackSave: true,
              },
            };

            const updatedResult = {
              ...toolCall.result,
              canvas: {
                ...toolCall.result?.canvas,
                ...updatedCanvasData,
              },
              canvasData: updatedCanvasData,
              success: true,
              message: `Canvas "${canvasState.name}" saved successfully (fallback)`,
            };

            return {
              ...toolCall,
              result: updatedResult,
            };
          }
          return toolCall;
        });

        if (!canvasToolCallFound) {
          const newCanvasToolCall = {
            id: `call_${Date.now()}_save`,
            type: "function",
            function: {
              name: "canvas_create",
              arguments: JSON.stringify({
                query: "Canvas content save",
                type: canvasState.type,
                name: canvasState.name,
                useStructuredStreaming: false,
              }),
            },
            result: {
              success: true,
              message: `Canvas "${canvasState.name}" saved successfully (fallback)`,
              canvas: {
                name: canvasState.name,
                type: canvasState.type,
                content: canvasState.content,
                version: canvasState.version + 1,
                language: canvasState.language,
                metadata: {
                  name: canvasState.name,
                  version: canvasState.version + 1,
                  lastSaved: new Date().toISOString(),
                  savedFromCanvas: true,
                  contentLength: canvasState.content?.length || 0,
                  fallbackSave: true,
                },
              },
              canvasData: {
                content: canvasState.content,
                contentType: canvasState.type,
                type: canvasState.type,
                language: canvasState.language,
                name: canvasState.name,
                title: canvasState.name,
                version: canvasState.version + 1,
                metadata: {
                  name: canvasState.name,
                  version: canvasState.version + 1,
                  lastSaved: new Date().toISOString(),
                  savedFromCanvas: true,
                  contentLength: canvasState.content?.length || 0,
                  fallbackSave: true,
                },
              },
            },
          };
          updatedToolCalls.push(newCanvasToolCall);
        }

        // Update the message in database
        const { error: updateError } = await supabase
          .from("thread_messages")
          .update({
            tool_calls: JSON.stringify(updatedToolCalls),
            updated_at: new Date().toISOString(),
            metadata: {
              ...(message.metadata || {}),
              lastCanvasSave: new Date().toISOString(),
              canvasVersion: canvasState.version + 1,
              fallbackSave: true,
            },
          })
          .eq("id", canvasState.sourceMessageId);

        if (updateError) {
          throw updateError;
        }

        // Update thread store
        const threadStore = useThreadStore.getState();
        if (threadStore.updateMessageState) {
          try {
            threadStore.updateMessageState({
              id: canvasState.sourceMessageId,
              tool_calls: updatedToolCalls,
              metadata: {
                ...(message.metadata || {}),
                lastCanvasSave: new Date().toISOString(),
                canvasVersion: canvasState.version + 1,
                fallbackSave: true,
              },
              updated_at: new Date().toISOString(),
            });
          } catch (updateStateError) {
            console.warn("Failed to update thread store:", updateStateError);
          }
        }

        // Update canvas state
        setCanvasState((prev) => ({
          ...prev,
          isDirty: false,
          isLoading: false,
          version: prev.version + 1,
          metadata: {
            ...prev.metadata,
            lastSaved: new Date().toISOString(),
            savedFromCanvas: true,
            fallbackSave: true,
            version: prev.version + 1,
          },
        }));

        console.log("🎨 [CanvasContext] Canvas saved using fallback method");
        return true;
      } catch (fallbackError) {
        console.error(
          "🎨 [CanvasContext] Fallback save failed:",
          fallbackError
        );
        throw fallbackError;
      }
    }
  }, [
    canvasState.thread_id,
    canvasState.workspace_id,
    canvasState.sourceMessageId,
    canvasState.content,
    canvasState.type,
    canvasState.language,
    canvasState.name,
    canvasState.version,
    canvasState.metadata,
    canvasState.canvasId,
  ]);

  // Reset canvas to original state
  const resetCanvas = useCallback(() => {
    setCanvasState((prev) => ({
      ...prev,
      content: prev.originalContent,
      isDirty: false,
      metadata: {
        ...prev.metadata,
        lastUpdated: new Date().toISOString(),
        lastUpdateSource: "reset",
      },
    }));
  }, []);

  // Copy canvas content to clipboard
  const copyCanvasContent = useCallback(async () => {
    try {
      const contentToCopy = canvasState.content || "";
      const canvasType = canvasState.type || "text";

      if (!contentToCopy.trim()) {
        return false;
      }

      let finalContentToCopy = contentToCopy;

      // Convert spreadsheet content to markdown table for copying
      if (canvasType === "spreadsheet") {
        // Convert CSV content to markdown table format
        const convertCsvToMarkdownTable = (csvContent) => {
          if (!csvContent || typeof csvContent !== "string") {
            return "";
          }

          try {
            const lines = csvContent.trim().split("\n");
            if (lines.length === 0) {
              return "";
            }

            const parseCSVLine = (line) => {
              const result = [];
              let current = "";
              let inQuotes = false;

              for (let i = 0; i < line.length; i++) {
                const char = line[i];
                const nextChar = line[i + 1];

                if (char === '"') {
                  if (inQuotes && nextChar === '"') {
                    current += '"';
                    i++;
                  } else {
                    inQuotes = !inQuotes;
                  }
                } else if (char === "," && !inQuotes) {
                  result.push(current.trim());
                  current = "";
                } else {
                  current += char;
                }
              }

              result.push(current.trim());
              return result;
            };

            const rows = lines.map(parseCSVLine);
            const nonEmptyRows = rows.filter((row) =>
              row.some((cell) => cell && cell.trim().length > 0)
            );

            if (nonEmptyRows.length === 0) {
              return "";
            }

            const markdownLines = [];

            nonEmptyRows.forEach((row, index) => {
              const escapedRow = row.map((cell) =>
                (cell || "").replace(/\|/g, "\\|")
              );

              markdownLines.push(`| ${escapedRow.join(" | ")} |`);

              if (index === 0) {
                const separators = escapedRow.map(() => "---");
                markdownLines.push(`| ${separators.join(" | ")} |`);
              }
            });

            return markdownLines.join("\n");
          } catch (error) {
            console.error("Error converting CSV to markdown table:", error);
            return csvContent;
          }
        };

        finalContentToCopy = convertCsvToMarkdownTable(contentToCopy);

        if (!finalContentToCopy.trim()) {
          finalContentToCopy = contentToCopy; // Fallback to original content
        }
      }

      await navigator.clipboard.writeText(finalContentToCopy);

      return true;
    } catch (error) {
      console.error("🎨 CanvasContext: Error copying content:", error);
      return false;
    }
  }, [canvasState.content, canvasState.type]);

  // Download canvas content as file
  const downloadCanvasContent = useCallback(() => {
    try {
      const contentToDownload = canvasState.content || "";
      const canvasType = canvasState.type || "text";

      // Determine file extension and MIME type based on canvas type
      const getFileExtension = (type) => {
        switch (type) {
          case "code":
            return "js";
          case "markdown":
            return "md";
          case "html":
            return "html";
          case "spreadsheet":
            return "csv";
          case "text":
          default:
            return "md";
        }
      };

      const getMimeType = (type) => {
        switch (type) {
          case "code":
            return "text/javascript";
          case "markdown":
            return "text/markdown";
          case "html":
            return "text/html";
          case "spreadsheet":
            return "text/csv";
          case "text":
          default:
            return "text/plain";
        }
      };

      const fileExtension = getFileExtension(canvasType);
      const mimeType = getMimeType(canvasType);
      const filename = `${canvasState.name || "canvas"}.${fileExtension}`;

      if (!contentToDownload.trim()) {
        console.warn("🎨 CanvasContext: No content to download");
        return false;
      }

      // Create and download file
      const blob = new Blob([contentToDownload], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error("🎨 CanvasContext: Error downloading content:", error);
      return false;
    }
  }, [canvasState.content, canvasState.name, canvasState.type]);

  // Rename canvas
  const renameCanvas = useCallback(
    async (newName) => {
      if (!newName || typeof newName !== "string" || !newName.trim()) {
        return false;
      }

      const trimmedName = newName.trim();
      const originalName = canvasState.name;

      // Update local state immediately for better UX
      setCanvasState((prev) => ({
        ...prev,
        name: trimmedName,
        isDirty: true,
        metadata: {
          ...prev.metadata,
          lastRenamed: new Date().toISOString(),
          previousName: prev.name,
        },
      }));

      // If we have a source message ID, save to database
      if (!canvasState.sourceMessageId) {
        // Even without source message, mark as not dirty since it's just a local rename
        setCanvasState((prev) => ({
          ...prev,
          isDirty: false,
        }));
        return true;
      }

      try {
        // Import required dependencies
        const { supabase } = await import("@/lib/supabase");
        const { useThreadStore } = await import("@/store");

        // Get the current message from database
        const { data: message, error: fetchError } = await supabase
          .from("thread_messages")
          .select("*")
          .eq("id", canvasState.sourceMessageId)
          .single();

        if (fetchError || !message) {
          throw new Error(
            `Failed to fetch message: ${fetchError?.message || "Message not found"}`
          );
        }

        // Parse existing tool_calls
        let toolCalls = [];
        if (message.tool_calls) {
          try {
            toolCalls =
              typeof message.tool_calls === "string"
                ? JSON.parse(message.tool_calls)
                : message.tool_calls;
          } catch (e) {
            console.error("Failed to parse existing tool_calls:", e);
            toolCalls = [];
          }
        }

        // Find and update the canvas tool call
        let canvasToolCallFound = false;
        const updatedToolCalls = toolCalls.map((toolCall) => {
          const toolName =
            toolCall.function?.name ||
            toolCall.name ||
            toolCall.type ||
            (toolCall.function && typeof toolCall.function === "string"
              ? toolCall.function
              : null);

          if (
            toolName === "canvas_create" ||
            toolName === "canvas_stream" ||
            toolName === "canvas_edit"
          ) {
            canvasToolCallFound = true;

            // Create updated canvas data with new name
            const updatedCanvasData = {
              ...toolCall.result.canvasData,
              name: trimmedName,
              title: trimmedName,
              metadata: {
                ...toolCall.result.canvasData?.metadata,
                lastRenamed: new Date().toISOString(),
                previousName: toolCall.result.canvasData?.name,
                renamedFromCanvas: true,
              },
            };

            const updatedCanvas = {
              ...toolCall.result.canvas,
              name: trimmedName,
              title: trimmedName,
              metadata: {
                ...toolCall.result.canvas?.metadata,
                lastRenamed: new Date().toISOString(),
                previousName: toolCall.result.canvas?.name,
                renamedFromCanvas: true,
              },
            };

            // Update the tool call result with new name
            const updatedResult = {
              ...toolCall.result,
              canvas: updatedCanvas,
              canvasData: updatedCanvasData,
            };

            return {
              ...toolCall,
              result: updatedResult,
            };
          }
          return toolCall;
        });

        if (!canvasToolCallFound) {
          // Revert local state on error
          setCanvasState((prev) => ({
            ...prev,
            name: originalName,
            isDirty: false,
            metadata: {
              ...prev.metadata,
              renameError: "Canvas tool call not found",
            },
          }));

          return false;
        }

        // Update the message in database
        const { error: updateError } = await supabase
          .from("thread_messages")
          .update({
            tool_calls: JSON.stringify(updatedToolCalls),
            updated_at: new Date().toISOString(),
            metadata: {
              ...(message.metadata || {}),
              lastCanvasRename: new Date().toISOString(),
              canvasName: trimmedName,
            },
          })
          .eq("id", canvasState.sourceMessageId);

        if (updateError) {
          throw updateError;
        }

        // Update thread store
        const threadStore = useThreadStore.getState();
        const storeUpdateData = {
          id: canvasState.sourceMessageId,
          tool_calls: updatedToolCalls,
          metadata: {
            ...(message.metadata || {}),
            lastCanvasRename: new Date().toISOString(),
            canvasName: trimmedName,
          },
          updated_at: new Date().toISOString(),
        };

        if (threadStore.updateMessageState) {
          try {
            threadStore.updateMessageState(storeUpdateData);
          } catch (updateStateError) {
            console.warn("Failed to update thread store:", updateStateError);
          }
        }

        // Update local canvas state to reflect successful database save
        setCanvasState((prev) => ({
          ...prev,
          name: trimmedName,
          isDirty: false, // Mark as not dirty since we saved to database
          metadata: {
            ...prev.metadata,
            lastRenamed: new Date().toISOString(),
            previousName: originalName,
            savedToDatabase: true,
            renamedFromCanvas: true,
          },
        }));

        return true;
      } catch (error) {
        console.error("🎨 CanvasContext: Error renaming canvas:", error);

        // Revert local state on error
        setCanvasState((prev) => ({
          ...prev,
          name: originalName, // Revert to original name
          isDirty: false,
          metadata: {
            ...prev.metadata,
            renameError: error.message,
            lastRenameAttempt: new Date().toISOString(),
          },
        }));

        return false;
      }
    },
    [canvasState.sourceMessageId, canvasState.name]
  );

  // Load a specific version of the canvas
  const loadCanvasVersion = useCallback(
    async (versionId) => {
      // First, try to find the version in local version history
      const localVersion = canvasState.versionHistory.find(
        (v) => v.id === versionId
      );

      if (localVersion) {
        setCanvasState((prev) => ({
          ...prev,
          content: localVersion.content,
          name: localVersion.name,
          version: localVersion.version,
          type: localVersion.type,
          language: localVersion.language,
          currentVersionId: versionId,
          isDirty: false,
          metadata: {
            ...prev.metadata,
            ...localVersion.metadata,
            loadedVersion: versionId,
            loadedAt: new Date().toISOString(),
          },
        }));

        return true;
      }

      // If not found locally, try to load from database
      try {
        const { supabase } = await import("@/lib/supabase");

        const { data: message, error } = await supabase
          .from("thread_messages")
          .select("*")
          .eq("id", versionId)
          .single();

        if (error || !message) {
          console.error("Failed to load version from database:", error);
          return false;
        }

        // Extract canvas data from tool calls
        let toolCalls = [];
        if (message.tool_calls) {
          try {
            toolCalls =
              typeof message.tool_calls === "string"
                ? JSON.parse(message.tool_calls)
                : message.tool_calls;
          } catch (e) {
            console.error("Failed to parse tool_calls:", e);
            toolCalls = [];
          }
        }

        // Find canvas tool call
        const canvasToolCall = toolCalls.find(
          (tc) =>
            tc.function?.name === "canvas_create" ||
            tc.function?.name === "canvas_stream" ||
            tc.function?.name === "canvas_edit"
        );

        if (!canvasToolCall || !canvasToolCall.result) {
          console.error("No canvas tool call found in message");
          return false;
        }

        // Extract canvas data with priority handling
        let canvasData = null;

        // Priority 1: Check for canvasData in result
        if (canvasToolCall.result.canvasData) {
          canvasData = canvasToolCall.result.canvasData;
        }
        // Priority 2: Check for canvas in result
        else if (canvasToolCall.result.canvas) {
          canvasData = canvasToolCall.result.canvas;
        }
        // Priority 3: Use the result itself if it has canvas properties
        else if (
          canvasToolCall.result.content ||
          canvasToolCall.result.blocks
        ) {
          canvasData = canvasToolCall.result;
        }

        if (!canvasData) {
          console.error("No canvas data found in tool call result");
          return false;
        }

        // Determine the best content to use with proper priority
        let contentToUse = null;

        // Priority 1: Use blocks if available and valid (for BlockNote compatibility)
        if (
          canvasData.blocks &&
          Array.isArray(canvasData.blocks) &&
          canvasData.blocks.length > 0
        ) {
          contentToUse = canvasData.blocks;
        }
        // Priority 2: Try to parse content as JSON blocks if it's a string
        else if (
          typeof canvasData.content === "string" &&
          canvasData.content.startsWith("[")
        ) {
          try {
            const parsedContent = JSON.parse(canvasData.content);
            if (Array.isArray(parsedContent) && parsedContent.length > 0) {
              contentToUse = parsedContent;
            }
          } catch (e) {
            console.warn("🎨 Failed to parse content as JSON blocks:", e);
          }
        }

        // Priority 3: Use content as array if it's already an array
        if (
          !contentToUse &&
          Array.isArray(canvasData.content) &&
          canvasData.content.length > 0
        ) {
          contentToUse = canvasData.content;
        }

        // Priority 4: Use string content as fallback
        if (
          !contentToUse &&
          typeof canvasData.content === "string" &&
          canvasData.content.length > 0
        ) {
          contentToUse = canvasData.content;
        }

        // Final fallback
        if (!contentToUse) {
          console.warn("🎨 No valid content found, using empty string");
          contentToUse = "";
        }

        // Update canvas state with the extracted data
        setCanvasState((prev) => ({
          ...prev,
          content: contentToUse,
          name:
            canvasData.metadata?.name ||
            canvasData.name ||
            canvasData.title ||
            "Canvas",
          version: canvasData.metadata?.version || canvasData.version || 1,
          type: canvasData.contentType || canvasData.type || "text",
          language: canvasData.language || "javascript",
          currentVersionId: versionId,
          isDirty: false,
          sourceMessageId: message.id, // Update source message ID to the loaded version
          metadata: {
            ...prev.metadata,
            ...canvasData.metadata,
            loadedVersion: versionId,
            loadedAt: new Date().toISOString(),
            loadedFromDatabase: true,
            originalMessageId: prev.sourceMessageId, // Keep track of original source
          },
          // Update version history to include this version if not already present
          versionHistory: prev.versionHistory.some((v) => v.id === versionId)
            ? prev.versionHistory
            : [
                ...prev.versionHistory,
                {
                  id: versionId,
                  version:
                    canvasData.metadata?.version || canvasData.version || 1,
                  name:
                    canvasData.metadata?.name ||
                    canvasData.name ||
                    canvasData.title ||
                    "Canvas",
                  content: contentToUse,
                  timestamp: message.created_at,
                  messageId: message.id,
                  type: canvasData.contentType || canvasData.type || "text",
                  language: canvasData.language || "javascript",
                  metadata: {
                    ...canvasData.metadata,
                    fromDatabase: true,
                    loadedFromVersion: true,
                  },
                },
              ],
        }));

        return true;
      } catch (error) {
        console.error("Error loading version from database:", error);
        return false;
      }
    },
    [canvasState.versionHistory, canvasState.sourceMessageId]
  );

  // Get all available versions for the current canvas
  const getAvailableVersions = useCallback(async () => {
    const versions = [...canvasState.versionHistory];

    // If we have a source message ID, try to get related versions from database
    if (canvasState.sourceMessageId) {
      try {
        const { supabase } = await import("@/lib/supabase");

        // Get current thread ID
        const { useThreadStore } = await import("@/store");
        const { currentThreadId } = useThreadStore.getState();

        if (currentThreadId) {
          const { data: messages, error } = await supabase
            .from("thread_messages")
            .select("id, created_at, tool_calls, metadata, content")
            .eq("thread_id", currentThreadId)
            .not("tool_calls", "is", null)
            .order("created_at", { ascending: false }); // Get newest first

          if (!error && messages) {
            for (const message of messages) {
              try {
                const toolCalls =
                  typeof message.tool_calls === "string"
                    ? JSON.parse(message.tool_calls)
                    : message.tool_calls;

                const canvasToolCall = toolCalls.find(
                  (tc) =>
                    tc.function?.name === "canvas_create" ||
                    tc.function?.name === "canvas_stream" ||
                    tc.function?.name === "canvas_edit"
                );

                if (canvasToolCall && canvasToolCall.result) {
                  // Extract canvas data with the same priority logic as loadCanvasVersion
                  let canvasData = null;

                  if (canvasToolCall.result.canvasData) {
                    canvasData = canvasToolCall.result.canvasData;
                  } else if (canvasToolCall.result.canvas) {
                    canvasData = canvasToolCall.result.canvas;
                  } else if (
                    canvasToolCall.result.content ||
                    canvasToolCall.result.blocks
                  ) {
                    canvasData = canvasToolCall.result;
                  }

                  if (canvasData) {
                    // Check if this version is already in our local history
                    const existsLocally = versions.some(
                      (v) => v.messageId === message.id || v.id === message.id
                    );

                    if (!existsLocally) {
                      // Determine content type and get preview
                      let contentPreview = "";
                      let hasBlocks = false;

                      if (
                        canvasData.blocks &&
                        Array.isArray(canvasData.blocks) &&
                        canvasData.blocks.length > 0
                      ) {
                        hasBlocks = true;
                        contentPreview = `${canvasData.blocks.length} blocks`;
                      } else if (typeof canvasData.content === "string") {
                        contentPreview =
                          canvasData.content.substring(0, 50) +
                          (canvasData.content.length > 50 ? "..." : "");
                      } else if (Array.isArray(canvasData.content)) {
                        contentPreview = `${canvasData.content.length} items`;
                      }

                      const versionEntry = {
                        id: message.id,
                        version:
                          canvasData.metadata?.version ||
                          canvasData.version ||
                          1,
                        name:
                          canvasData.metadata?.name ||
                          canvasData.name ||
                          canvasData.title ||
                          "Canvas",
                        timestamp: message.created_at,
                        messageId: message.id,
                        type:
                          canvasData.contentType || canvasData.type || "text",
                        language: canvasData.language || "javascript",
                        metadata: {
                          ...canvasData.metadata,
                          fromDatabase: true,
                          toolCallType: canvasToolCall.function?.name,
                          hasBlocks,
                          contentPreview,
                          hasContent: !!(
                            canvasData.content || canvasData.blocks
                          ),
                          contentLength:
                            typeof canvasData.content === "string"
                              ? canvasData.content.length
                              : 0,
                          blocksCount: canvasData.blocks?.length || 0,
                        },
                      };

                      versions.push(versionEntry);
                    }
                  }
                }
              } catch (e) {
                console.warn("Failed to parse message tool calls:", e);
              }
            }
          }
        }
      } catch (error) {
        console.warn("Failed to load versions from database:", error);
      }
    }

    // Sort versions by timestamp (newest first) and remove duplicates
    const uniqueVersions = versions.reduce((acc, version) => {
      const existing = acc.find(
        (v) => v.id === version.id || v.messageId === version.messageId
      );
      if (!existing) {
        acc.push(version);
      }
      return acc;
    }, []);

    const sortedVersions = uniqueVersions.sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );

    return sortedVersions;
  }, [canvasState.versionHistory, canvasState.sourceMessageId]);

  const value = {
    canvasState,
    editorState,
    collaborationState,
    showPreview,
    setShowPreview,
    openCanvas,
    closeCanvas,
    updateContent,
    saveCanvas,
    resetCanvas,
    copyCanvasContent,
    downloadCanvasContent,
    setEditorState,
    setCollaborationState,
    // Convenience properties for easier access
    canvasContent: canvasState.content,
    canvasName: canvasState.name,
    canvasVersion: canvasState.version,
    canvasType: canvasState.type,
    canvasLanguage: canvasState.language,
    isCanvasOpen: canvasState.isOpen,
    canvasIsDirty: canvasState.isDirty,
    canvasIsLoading: canvasState.isLoading,
    canvasError: canvasState.error,
    canvasSourceMessageId: canvasState.sourceMessageId,
    canvasVersionHistory: canvasState.versionHistory,
    canvasCurrentVersionId: canvasState.currentVersionId,
    loadCanvasVersion,
    getAvailableVersions,
    renameCanvas,
    // Check if a message was manually closed
    isManuallyClosed: (messageId) => {
      return manuallyClosedMessages.has(messageId);
    },
  };

  return (
    <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>
  );
};
